<template>
    <div>
        <!-- Modal para historial de lecturas -->
        <b-modal ref="historialLecturas" size="xl" hide-footer no-close-on-backdrop>
            <!-- Header personalizado -->
            <template #modal-title>
                <div class="d-flex align-items-center">
                    <i class="fas fa-chart-line text-primary mr-2"></i>
                    <span class="font-weight-bold">Historial de Lecturas</span>
                </div>
            </template>
            
            <!-- Loader personalizado para descarga -->
            <div v-if="generandoCsv" class="loader-overlay">
                <div class="loader-content">
                    <div class="spinner-border text-success" role="status" style="width: 3rem; height: 3rem;">
                        <span class="sr-only">Generando...</span>
                    </div>
                    <h5 class="mt-3 text-center">Generando archivo CSV...</h5>
                    <p class="text-muted text-center">Por favor espere mientras procesamos los datos</p>
                    <div class="progress mt-3" style="height: 8px;">
                        <div class="progress-bar progress-bar-striped progress-bar-animated bg-success" 
                             role="progressbar" style="width: 100%"></div>
                    </div>
                </div>
            </div>
            
            <div class="filters-container">
                <!-- Primera fila de filtros -->
                <div class="row mb-3">
                    <!-- Filtro por fecha -->
                    <div class="col-md-4 col-sm-6 mb-3">
                        <label for="fecha-filtro" class="form-label">
                            <i class="fas fa-calendar-alt text-primary mr-1"></i>
                            Fecha:
                        </label>
                        <b-form-input
                            id="fecha-filtro"
                            type="date"
                            v-model="fechaFiltro"
                            class="form-control-custom"
                        ></b-form-input>
                    </div>
                    <!-- Filtro por hora inicial -->
                    <div class="col-md-4 col-sm-6 mb-3">
                        <label for="hora-inicial" class="form-label">
                            <i class="fas fa-clock text-info mr-1"></i>
                            Hora inicial:
                        </label>
                        <b-form-input
                            id="hora-inicial"
                            type="time"
                            v-model="horaInicial"
                            class="form-control-custom"
                        ></b-form-input>
                    </div>
                    <!-- Filtro por hora final -->
                    <div class="col-md-4 col-sm-6 mb-3">
                        <label for="hora-final" class="form-label">
                            <i class="fas fa-clock text-info mr-1"></i>
                            Hora final:
                        </label>
                        <b-form-input
                            id="hora-final"
                            type="time"
                            v-model="horaFinal"
                            class="form-control-custom"
                        ></b-form-input>
                    </div>
                </div>
                
                <!-- Botones de acción -->
                <div class="row mb-3">
                    <div class="col-12 text-center">
                        <b-button 
                            variant="primary" 
                            @click="filtrarPorFechaHora" 
                            :disabled="loaderTabla || generandoCsv" 
                            class="btn-custom btn-filter mr-2 mb-2"
                            size="sm"
                        >
                            <i class="fas fa-search mr-1"></i>
                            Filtrar
                        </b-button>
                        <b-button 
                            variant="outline-secondary" 
                            @click="limpiarFiltro" 
                            :disabled="loaderTabla || generandoCsv" 
                            class="btn-custom mr-2 mb-2"
                            size="sm"
                        >
                            <i class="fas fa-eraser mr-1"></i>
                            Limpiar
                        </b-button>
                        <b-button 
                            variant="outline-info" 
                            @click="refrescarDatos" 
                            :disabled="loaderTabla || generandoCsv" 
                            class="btn-custom mr-2 mb-2"
                            size="sm"
                        >
                            <i class="fas fa-sync-alt mr-1"></i>
                            Actualizar
                        </b-button>
                        <b-button 
                            variant="success" 
                            @click="descargarCsv" 
                            :disabled="loaderTabla || generandoCsv || (!hayDatos)" 
                            class="btn-custom btn-download mr-2 mb-2"
                            size="sm"
                        >
                            <i class="fas fa-download mr-1"></i>
                            Descargar CSV
                        </b-button>
                    </div>
                </div>
            </div>
            <b-alert
                v-if="mensajeAlerta"
                variant="warning"
                show
                class="mb-3"
            >
                {{ mensajeAlerta }}
            </b-alert>
            <div class="table-responsive">
                <div v-if="loaderTabla" class="d-flex justify-content-center align-items-center" style="height: 200px;">
                    <b-spinner label="Cargando..." type="grow" variant="primary"></b-spinner>
                </div>
                <b-table
                    v-else
                    :items="registrosFiltrados.length > 0 ? registrosFiltrados : historialOperaciones"
                    :fields="fieldsLecturas"
                    :per-page="opcionesPaginate.perPage"
                    :current-page="opcionesPaginate.currentPage"
                    striped hover small responsive
                    id="table-lecturas"
                >
                    <template #cell(id)="data">
                        {{ data.index + 1  }}
                    </template>
                    <template #cell(fecha)="data">
                        {{ formatFechaColombia(data.item.timestamp) }}
                    </template>
                    <template #cell(hora)="data">
                        {{ formatHoraColombia(data.item.timestamp) }}
                    </template>
                </b-table>
                <b-pagination
                    v-model="paginaActual"
                    :total-rows="opcionesPaginate.total"
                    :per-page="opcionesPaginate.perPage"
                    aria-controls="table-lecturas"
                    @change="cambiarPagina"
                >
                </b-pagination>
            </div>
        </b-modal>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    data() {
        return {
            historialOperaciones: [],
            fieldsLecturas: [
                { key: 'id', label: 'Item' },
                { key: 'fecha', label: 'Fecha' },
                { key: 'hora', label: 'Hora' },
                { key: 'estado', label: 'Estado' },
                { key: 'temperatura_ds18b20', label: 'Temp. Aire (°C)' },
                { key: 'temperatura_ambiente', label: 'Temp. Ambiente (°C)' },
                { key: 'humedad_ambiente', label: 'Humedad (%)' },
                { key: 'voltaje_interior', label: 'Voltaje Interior (V)' },
                { key: 'corriente_interior', label: 'Corriente Interior (A)' },
                { key: 'voltaje_exterior', label: 'Voltaje Exterior (V)' },
                { key: 'corriente_exterior', label: 'Corriente Exterior (A)' },
                { key: 'fp_exterior', label: 'Factor Potencia' },
                { key: 'frecuencia_exterior', label: 'Frecuencia (Hz)' }
            ],
            fechasDisponibles: [],
            fechaFiltro: '',
            horaInicial: '',
            horaFinal: '',
            loaderTabla: false,
            opcionesPaginate: {},
            registrosFiltrados: [],
            paginaActual: 1,
            totalRegistros: 0,
            itemUnidad: null,
            mensajeAlerta: '',
            generandoCsv: false
        }
    },
    computed: {
        hayDatos() {
            return (this.registrosFiltrados.length > 0) || (this.historialOperaciones.length > 0);
        }
    },
    methods: {
        mostrarHistorialLecturas(item) {
            this.itemUnidad = item;
            this.paginaActual = 1;
            this.cargarPagina();
            this.$refs['historialLecturas'].show();
        },
        async cargarPagina() {
            this.loaderTabla = true;
            // Llama a la API paginada, ejemplo: /api/get-lecturas/:cbi?page=:paginaActual
            try {
                const response = await axios.get(`api/get-lecturas/${this.itemUnidad.cbi}?page=${this.paginaActual}`, {
                    headers: { Authorization: `Bearer ${this.$store.state.user.token}` }
                });
                // console.log(response.data);
                this.historialOperaciones = response.data.lecturas.data;
                this.opcionesPaginate = response.data.lecturas;
                this.totalRegistros = response.data.lecturas.total; // total de registros en la API
                this.registrosFiltrados = [];
                this.fechaFiltro = '';
                this.horaInicial = '';
                this.horaFinal = '';
                // this.mostrarFechasDisponibles();
            } catch (error) {
                console.error('Error fetching historial de operaciones:', error);
            }
            this.loaderTabla = false;
        },
        filtrarPorFechaHora(page = 1) {
            // Validar que al menos haya una fecha seleccionada
            if (!this.fechaFiltro) {
                this.mensajeAlerta = 'Debes seleccionar al menos una fecha para filtrar.';
                this.registrosFiltrados = [];
                return;
            }
            
            // Validar rango de horas si ambas están definidas
            if (this.horaInicial && this.horaFinal && this.horaInicial > this.horaFinal) {
                this.mensajeAlerta = 'La hora inicial no puede ser mayor a la hora final.';
                return;
            }
            
            this.loaderTabla = true;
            this.mensajeAlerta = '';
            
            const params = {
                cbi: this.itemUnidad.cbi,
                fecha: this.fechaFiltro,
                hora_inicial: this.horaInicial || '00:00:00',
                hora_final: this.horaFinal || '23:59:59',
                page: page,
                paginated: 'si'  // Mantener paginación en 10 registros por página
            };
            
            axios.post(`api/get-lecturas-filtradas`, params, {
                headers: { Authorization: `Bearer ${this.$store.state.user.token}` }
            })
            .then(response => {
                // Si la respuesta es paginada, actualiza los datos y la paginación
                // console.log(response.data);
                this.registrosFiltrados = response.data.lecturas.data;
                this.opcionesPaginate = response.data.lecturas;
                this.totalRegistros = response.data.lecturas.total;
                this.paginaActual = response.data.lecturas.current_page;
                
                if (this.registrosFiltrados.length === 0) {
                    this.mensajeAlerta = 'No se encontraron registros con los filtros aplicados.';
                }
            })
            .catch(error => {
                console.error('Error filtrando historial de operaciones:', error);
                this.registrosFiltrados = [];
                this.mensajeAlerta = 'Error al filtrar los datos. Intenta nuevamente.';
            })            
            .finally(() => {
                this.loaderTabla = false;
            });
        },
        cambiarPagina(page) {
            if (this.registrosFiltrados.length > 0 || this.fechaFiltro) {
                // Si hay filtro activo, paginar sobre los filtrados
                this.filtrarPorFechaHora(page);
            } else {
                // Si no hay filtro, paginar sobre todos
                this.paginaActual = page;
                this.cargarPagina();
            }
        },
        limpiarFiltro() {
            this.paginaActual = 1;
            this.fechaFiltro = '';
            this.horaInicial = '';
            this.horaFinal = '';
            this.registrosFiltrados = [];
            this.mensajeAlerta = '';
            this.cargarPagina();
        },
        
        refrescarDatos() {
            if (this.registrosFiltrados.length > 0 || this.fechaFiltro) {
                // Si hay filtros aplicados, volver a filtrar
                this.filtrarPorFechaHora(this.paginaActual);
            } else {
                // Si no hay filtros, cargar página actual
                this.cargarPagina();
            }
        },
        
        async descargarCsv() {
            console.log('Iniciando descarga de CSV...');
            this.loaderTabla = true;
            this.generandoCsv = true;
            
            try {
                let datos = [];
                
                if (this.fechaFiltro !== '') {
                    // Si HAY filtros aplicados, usar API de filtros
                    const params = {
                        cbi: this.itemUnidad.cbi,
                        fecha: this.fechaFiltro,
                        hora_inicial: this.horaInicial || '00:00:00',
                        hora_final: this.horaFinal || '23:59:59',
                        paginated: 'no' // Solicitar todos los registros sin paginación
                    };
                    
                    const response = await axios.post(`/api/get-lecturas-filtradas`, params, {
                        headers: { Authorization: `Bearer ${this.$store.state.user.token}` }
                    });
                    
                    // Extraer datos de la respuesta
                    if (response.data.lecturas) {
                        datos = response.data.lecturas.data || response.data.lecturas;
                    } else if (response.data.data) {
                        datos = response.data.data;
                    } else {
                        datos = response.data;
                    }
                    
                    // Si no obtuvimos datos de la API, usar los datos filtrados que ya tenemos
                    if (!Array.isArray(datos) || datos.length === 0) {
                        datos = this.registrosFiltrados;
                    }
                    
                } else {
                    // Si NO hay filtros, obtener TODOS los datos sin paginación
                    const response = await axios.get(`/api/get-lecturas/${this.itemUnidad.cbi}/2`, {
                        headers: { Authorization: `Bearer ${this.$store.state.user.token}` }
                    });
                    
                    // Extraer datos de la respuesta
                    if (response.data.lecturas) {
                        datos = response.data.lecturas.data || response.data.lecturas;
                    } else if (response.data.data) {
                        datos = response.data.data;
                    } else {
                        datos = response.data;
                    }
                    
                    // Si no obtuvimos datos de la API, usar los datos actuales que ya tenemos
                    if (!Array.isArray(datos) || datos.length === 0) {
                        datos = this.historialOperaciones;
                    }
                }
                
                // Verificar si datos es un array
                if (!Array.isArray(datos)) {
                    console.error('Los datos no son un array:', datos);
                    datos = [];
                }
                
                if (datos.length === 0) {
                    if (window.Swal) {
                        Swal.fire({
                            icon: 'warning',
                            title: 'Sin datos',
                            text: 'No hay datos para descargar'
                        });
                    } else {
                        alert('No hay datos para descargar');
                    }
                    return;
                }
                
                // Preparar datos para CSV con todos los campos de la tabla lecturas
                const encabezados = [
                    'Item',
                    'CBI',
                    'Estado', 
                    // 'Timestamp',
                    'Fecha',
                    'Hora',
                    'Voltaje Exterior (V)',
                    'Corriente Exterior (A)',
                    'Potencia Exterior (W)',
                    'Factor Potencia Exterior',
                    'Frecuencia Exterior (Hz)',
                    'Voltaje Interior (V)',
                    'Corriente Interior (A)',
                    'Potencia Interior (W)',
                    'Factor Potencia Interior',
                    'Frecuencia Interior (Hz)',
                    'Temp. DS18B20 (°C)',
                    'Temp. Ambiente (°C)',
                    'Humedad Ambiente (%)',
                    'Potencia Total (W)',
                    'Energía Total (Wh)',
                    'Tiempo On (segundos)'
                ];
                
                // Convertir datos a filas CSV
                const filasCsv = datos.map((item, index) => [
                    index + 1,
                    item.cbi || '',
                    item.estado || '',
                    // item.timestamp || '',
                    this.formatFechaColombia(item.timestamp),
                    this.formatHoraColombia(item.timestamp),
                    item.voltaje_exterior || '',
                    item.corriente_exterior || '',
                    item.potencia_exterior || '',
                    item.fp_exterior || '',
                    item.frecuencia_exterior || '',
                    item.voltaje_interior || '',
                    item.corriente_interior || '',
                    item.potencia_interior || '',
                    item.fp_interior || '',
                    item.frecuencia_interior || '',
                    item.temperatura_ds18b20 || '',
                    item.temperatura_ambiente || '',
                    item.humedad_ambiente || '',
                    item.potencia_total || '',
                    item.energia_total || '',
                    item.tiempo_on || ''
                ]);
                
                // Crear contenido CSV con BOM para UTF-8
                const BOM = '\uFEFF'; // BOM para UTF-8
                let csvContent = BOM + encabezados.join(',') + '\n';
                filasCsv.forEach(fila => {
                    csvContent += fila.map(campo => {
                        // Convertir a string y manejar valores nulos
                        let valor = campo === null || campo === undefined ? '' : String(campo);
                        
                        // Escapar campos que contengan comas, comillas o caracteres especiales
                        if (valor.includes(',') || valor.includes('"') || valor.includes('\n') || valor.includes('\r')) {
                            return `"${valor.replace(/"/g, '""')}"`;
                        }
                        return valor;
                    }).join(',') + '\n';
                });
                
                // Generar nombre de archivo
                const fechaActual = new Date().toISOString().split('T')[0];
                let nombreArchivo = `Historial_Lecturas_${this.itemUnidad.cbi}_${fechaActual}`;
                
                if (this.fechaFiltro) {
                    nombreArchivo += `_${this.fechaFiltro}`;
                    
                    if (this.horaInicial && this.horaFinal) {
                        nombreArchivo += `_${this.horaInicial.replace(/:/g, '')}_a_${this.horaFinal.replace(/:/g, '')}`;
                    } else if (this.horaInicial) {
                        nombreArchivo += `_desde_${this.horaInicial.replace(/:/g, '')}`;
                    } else if (this.horaFinal) {
                        nombreArchivo += `_hasta_${this.horaFinal.replace(/:/g, '')}`;
                    }
                }
                
                nombreArchivo += '.csv';
                
                // Crear y descargar archivo CSV con codificación UTF-8
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', nombreArchivo);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                // Mostrar mensaje de éxito
                if (window.Swal) {
                    Swal.fire({
                        icon: 'success',
                        title: '¡Éxito!',
                        text: `Archivo ${nombreArchivo} descargado correctamente (${datos.length} registros)`,
                        timer: 3000,
                        showConfirmButton: false
                    });
                } else {
                    alert(`Éxito: Archivo ${nombreArchivo} descargado correctamente (${datos.length} registros)`);
                }
                
            } catch (error) {
                console.error('Error descargando CSV:', error);
                let mensajeError = 'Error al descargar el archivo CSV';
                
                if (error.response) {
                    // Error de respuesta HTTP
                    if (error.response.status === 404) {
                        mensajeError = 'La API para descargar datos no está disponible';
                    } else if (error.response.status === 500) {
                        mensajeError = 'Error interno del servidor al obtener los datos';
                    } else {
                        mensajeError = `Error HTTP ${error.response.status}: ${error.response.data?.message || error.message}`;
                    }
                } else {
                    mensajeError = `Error: ${error.message}`;
                }
                
                if (window.Swal) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: mensajeError
                    });
                } else {
                    alert(`Error: ${mensajeError}`);
                }
            } finally {
                this.loaderTabla = false;
                this.generandoCsv = false;
            }
        },
        
        formatFechaColombia(ts) {
            if (!ts) return '';
            const fecha = new Date(ts);
            return fecha.toLocaleDateString('es-CO', { timeZone: 'America/Bogota' });
        },
        formatHoraColombia(ts) {
            if (!ts) return '';
            const fecha = new Date(ts);
            return fecha.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'America/Bogota' });
        }
    }
}
</script>

<style scoped>
.filters-container {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-radius: 10px;
    padding: 20px;
    margin-bottom: 20px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.form-label {
    font-weight: 600;
    color: #495057;
    margin-bottom: 8px;
}

.form-control-custom {
    border: 2px solid #e9ecef;
    border-radius: 8px;
    padding: 10px 12px;
    transition: all 0.3s ease;
}

.form-control-custom:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0,123,255,0.25);
    transform: translateY(-1px);
}

.btn-custom {
    border-radius: 25px;
    padding: 8px 20px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.3s ease;
    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.btn-custom:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0,0,0,0.2);
}

.btn-filter {
    background: linear-gradient(45deg, #007bff, #0056b3);
    border: none;
}

.btn-download {
    background: linear-gradient(45deg, #28a745, #1e7e34);
    border: none;
}

.loader-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.loader-content {
    background: white;
    padding: 40px;
    border-radius: 15px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.3);
    text-align: center;
    min-width: 300px;
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from { opacity: 0; transform: scale(0.9); }
    to { opacity: 1; transform: scale(1); }
}

.progress {
    border-radius: 10px;
    overflow: hidden;
}

.modal-title {
    font-size: 1.25rem;
}
</style>